import { useRef, useState, useEffect } from "react";
import { api, API } from "@/lib/apiClient";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader, formatDate } from "@/components/shared";
import { Upload, Download, Repeat, Sparkles, FileSpreadsheet, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export default function AdminToolsPage() {
  const [tenantResult, setTenantResult] = useState(null);
  const [lotResult, setLotResult] = useState(null);
  const [recurringResult, setRecurringResult] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const [crmLog, setCrmLog] = useState([]);
  const tenantFileRef = useRef(null);
  const lotFileRef = useRef(null);

  const loadCrmLog = async () => {
    try { const { data } = await api.get("/integrations/ghl/log?limit=50"); setCrmLog(data); }
    catch (e) {
      // GHL not configured for this park is the normal case — 404/501. Log anything else.
      if (e?.response?.status && ![404, 501].includes(e.response.status)) {
        console.warn("CRM log load failed:", e);
      }
    }
  };
  useEffect(() => { loadCrmLog(); }, []);

  const downloadTemplate = (kind) => {
    const url = `${API}/admin/import/${kind}/template.csv`;
    window.open(url, "_blank");
  };

  const uploadCsv = async (kind, file, setResult) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/admin/import/${kind}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success(`${data.created} ${kind} imported${data.errors.length ? ` (${data.errors.length} skipped)` : ""}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    }
  };

  const runRecurring = async () => {
    try {
      const { data } = await api.post("/admin/recurring-invoices/run");
      setRecurringResult(data);
      toast.success(`Generated ${data.created} invoices for ${data.month}`);
    } catch { toast.error("Failed"); }
  };

  const seedDemo = async () => {
    if (!window.confirm("Re-seed demo data? This wipes existing lots/tenants/reservations/invoices.")) return;
    setSeeding(true);
    try {
      const { data } = await api.post("/admin/seed-demo");
      toast.success(`Re-seeded ${data.lots} lots · ${data.tenants} tenants · ${data.reservations} reservations`);
    } catch { toast.error("Failed"); }
    finally { setSeeding(false); }
  };

  return (
    <div data-testid="admin-tools-page">
      <PageHeader title="Admin Tools" subtitle="Data import, recurring billing, and demo seeding" />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="border-[#EAEAE5] shadow-none"><CardContent className="p-6 space-y-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#737A76] mb-1">Tenants Import</div>
            <p className="text-sm text-[#737A76]">Import tenants from a CSV exported from FileMaker (or any source). Download the template, fill it in, then upload.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" className="border-[#EAEAE5]" onClick={() => downloadTemplate("tenants")} data-testid="download-tenants-template">
              <Download size={14} className="mr-2" /> Template
            </Button>
            <Button variant="outline" className="border-[#EAEAE5]" onClick={() => tenantFileRef.current?.click()} data-testid="upload-tenants-button">
              <Upload size={14} className="mr-2" /> Upload CSV
            </Button>
            <input type="file" accept=".csv" ref={tenantFileRef} className="hidden" onChange={(e) => uploadCsv("tenants", e.target.files?.[0], setTenantResult)} />
          </div>
          {tenantResult && (
            <div className="text-xs bg-[#FAFAF7] border border-[#EAEAE5] rounded-md p-3" data-testid="tenants-import-result">
              <div className="font-medium text-[#297343]">{tenantResult.created} tenants imported</div>
              {tenantResult.errors?.length > 0 && (
                <details className="mt-1">
                  <summary className="cursor-pointer text-[#A13E34]">{tenantResult.errors.length} errors</summary>
                  <ul className="mt-2 space-y-0.5 text-[#737A76]">{tenantResult.errors.slice(0, 20).map((e, i) => <li key={i}>{e}</li>)}</ul>
                </details>
              )}
            </div>
          )}
        </CardContent></Card>

        <Card className="border-[#EAEAE5] shadow-none"><CardContent className="p-6 space-y-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#737A76] mb-1">Lots Import</div>
            <p className="text-sm text-[#737A76]">Import lot inventory from a CSV. Columns include utilities, rates, and grid position for the park map.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" className="border-[#EAEAE5]" onClick={() => downloadTemplate("lots")} data-testid="download-lots-template">
              <Download size={14} className="mr-2" /> Template
            </Button>
            <Button variant="outline" className="border-[#EAEAE5]" onClick={() => lotFileRef.current?.click()} data-testid="upload-lots-button">
              <Upload size={14} className="mr-2" /> Upload CSV
            </Button>
            <input type="file" accept=".csv" ref={lotFileRef} className="hidden" onChange={(e) => uploadCsv("lots", e.target.files?.[0], setLotResult)} />
          </div>
          {lotResult && (
            <div className="text-xs bg-[#FAFAF7] border border-[#EAEAE5] rounded-md p-3" data-testid="lots-import-result">
              <div className="font-medium text-[#297343]">{lotResult.created} lots imported</div>
              {lotResult.errors?.length > 0 && (
                <details className="mt-1">
                  <summary className="cursor-pointer text-[#A13E34]">{lotResult.errors.length} errors</summary>
                  <ul className="mt-2 space-y-0.5 text-[#737A76]">{lotResult.errors.slice(0, 20).map((e, i) => <li key={i}>{e}</li>)}</ul>
                </details>
              )}
            </div>
          )}
        </CardContent></Card>

        <Card className="border-[#EAEAE5] shadow-none"><CardContent className="p-6 space-y-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#737A76] mb-1">Recurring Monthly Invoices</div>
            <p className="text-sm text-[#737A76]">Generate this month's lot-rent invoices for all active long-term tenants who don't already have one.</p>
          </div>
          <Button className="bg-[#284B3B] hover:bg-[#1E3F32] text-white" onClick={runRecurring} data-testid="run-recurring-button">
            <Repeat size={14} className="mr-2" /> Generate monthly invoices
          </Button>
          {recurringResult && (
            <div className="text-xs bg-[#FAFAF7] border border-[#EAEAE5] rounded-md p-3" data-testid="recurring-result">
              <span className="text-[#297343] font-medium">{recurringResult.created} created</span>
              <span className="text-[#737A76]"> · {recurringResult.skipped} skipped · period {recurringResult.month}</span>
            </div>
          )}
        </CardContent></Card>

        <Card className="border-[#EAEAE5] shadow-none"><CardContent className="p-6 space-y-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#737A76] mb-1">Demo Data</div>
            <p className="text-sm text-[#737A76]">Reset the park to a fresh demo dataset: 48 lots, 12 tenants, 12 reservations.</p>
          </div>
          <Button variant="outline" className="border-[#EAEAE5]" onClick={seedDemo} disabled={seeding} data-testid="seed-demo-button">
            <Sparkles size={14} className="mr-2" /> {seeding ? "Seeding…" : "Re-seed demo"}
          </Button>
        </CardContent></Card>
      </div>

      <Card className="border-[#EAEAE5] shadow-none mt-4"><CardContent className="p-6 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#737A76] mb-1">FreedomCRM Sync Log</div>
            <p className="text-xs text-[#737A76]">Recent push/pull activity between Park Director and your FreedomCRM subaccount.</p>
          </div>
          <Button variant="outline" size="sm" className="border-[#EAEAE5]" onClick={loadCrmLog} data-testid="refresh-crm-log"><RefreshCw size={12} className="mr-1" /> Refresh</Button>
        </div>
        {crmLog.length === 0 ? (
          <div className="text-sm text-[#737A76] py-6 text-center">No sync activity yet. Configure FreedomCRM in Park Setup and push a tenant.</div>
        ) : (
          <table className="w-full text-sm pd-table">
            <thead><tr className="text-left border-b border-[#EAEAE5]"><th className="py-2">When</th><th>Direction</th><th>Kind</th><th>Status</th><th>Summary</th></tr></thead>
            <tbody>
              {crmLog.map((row) => (
                <tr key={row.id} className="border-b border-[#F0F0EC] last:border-0">
                  <td className="py-2 text-[#737A76]">{formatDate(row.created_at)}</td>
                  <td><span className="text-xs uppercase tracking-wider text-[#737A76]">{row.direction}</span></td>
                  <td>{row.kind}</td>
                  <td><span className={`text-[11px] px-2 py-0.5 rounded-full ${row.status === "ok" ? "bg-[#E3F0E6] text-[#297343]" : "bg-[#F5E3E0] text-[#A13E34]"}`}>{row.status}</span></td>
                  <td className="text-[#737A76]">{row.summary}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent></Card>
    </div>
  );
}
